import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EstimatedTradeCostDataState } from './EstimateTradeCostDataState';
import State from 'store/State';
import { selectWidgetsDataState } from 'widgets/store/selectors';
import {
    EstimatedTradeCostRequest,
    toUid,
} from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import { EstimatedTradeCostApiRequest } from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApiRequest';
import { EstimatedTradeCostApiResponse } from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApiResponse';
import { validateResponse } from 'widgets/estimatedTradeCost/api/validation';

const initialState: EstimatedTradeCostDataState = {};

const estimatedCostDataSlice = createSlice({
    name: 'estimatedCostDataSlice',
    initialState,
    reducers: {
        addRequest: (
            state,
            action: PayloadAction<{
                request: EstimatedTradeCostRequest;
                apiRequest: EstimatedTradeCostApiRequest;
            }>
        ) => {
            const { request, apiRequest } = action.payload;
            const uid = toUid(request);
            state[uid] = {
                request,
                apiRequest,
                requestTimestamp: new Date().getTime(),
                status: 'In Progress',
            };
        },
        addResponse: (
            state,
            action: PayloadAction<{
                request: EstimatedTradeCostRequest;
                response: EstimatedTradeCostApiResponse;
            }>
        ) => {
            const {
                payload: { request, response },
            } = action;
            const uid = toUid(request);
            if (!state[uid]) {
                throw new Error('No request for uid ' + uid);
            }
            state[uid].response = response;
            state[uid].responseTimestamp = new Date().getTime();

            const responseValidationResult = validateResponse(response);
            if (responseValidationResult.isValid) {
                state[uid].status = 'Completed';
            } else {
                state[uid].status = 'Failed';
                state[uid].error = responseValidationResult.error;
            }
        },
        clearData: (
            state,
            action: PayloadAction<
                | {
                      request: EstimatedTradeCostRequest;
                  }
                | string
            >
        ) => {
            if (typeof action.payload === 'string') {
                delete state[action.payload];
                return;
            }
            const {
                payload: { request },
            } = action;
            const uid = toUid(request);
            delete state[uid];
        },

        addError: (
            state,
            action: PayloadAction<{
                request: EstimatedTradeCostRequest;
                error: unknown;
            }>
        ) => {
            const {
                payload: { error, request },
            } = action;
            const uid = toUid(request);
            state[uid].error = error;
            state[uid].status = 'Failed';
            state[uid].responseTimestamp = new Date().getTime();
        },
    },
});

export default estimatedCostDataSlice.reducer;

export const { addError, addRequest, addResponse, clearData } =
    estimatedCostDataSlice.actions;

export const selectEstimatedTradeCostWidgetsData = (state: State) =>
    selectWidgetsDataState(state).estimatedTradeCost;

export const selectEstimatedTradeCostState = (state: State, uid: string) =>
    uid ? selectEstimatedTradeCostWidgetsData(state)[uid] : undefined;
